import { useLocation } from "wouter";
import SEOHead from "@/components/SEOHead";
import SearchHeader from "@/components/search-header";
import Footer from "@/components/footer";
import { useState, useEffect } from "react";
import { loadAllArtworks, searchArtworks, getRandomArtwork, StaticArtwork } from "@/lib/static-data";
import { trackEvent } from "@/lib/analytics";

export default function PrivacyPolicy() {
  const [, setLocation] = useLocation();
  const [comprehensiveArtworks, setComprehensiveArtworks] = useState<
    StaticArtwork[]
  >([]);
  const [searchResults, setSearchResults] = useState<StaticArtwork[]>([]);

  useEffect(() => {
    async function loadInitialData() {
      try {
        const artworks = await loadAllArtworks();
        setComprehensiveArtworks(artworks);
      } catch (error) {
        console.error("Error loading artworks:", error);
      }
    }
    loadInitialData();
  }, []);

  const handleArtworkSelect = (artworkId: number) => {
    window.history.pushState({}, "", `/artwork/${artworkId}/`);
    window.dispatchEvent(new PopStateEvent("popstate"));
    trackEvent("view_artwork", "navigation", "search_result", artworkId);
  };

  const handleSearch = (query: string) => {
    if (!query.trim()) {
      setSearchResults([]);
      return;
    }
    const results = searchArtworks(comprehensiveArtworks, query, 20);
    setSearchResults(results);
    trackEvent("search", "artwork_search", query, results.length);
  };

  const handleRandomArtwork = () => {
    if (comprehensiveArtworks.length > 0) {
      const randomArtwork = getRandomArtwork(comprehensiveArtworks);
      if (randomArtwork && randomArtwork.id) {
        window.history.pushState({}, "", `/artwork/${randomArtwork.id}/`);
        window.dispatchEvent(new PopStateEvent("popstate"));
        trackEvent("view_artwork", "navigation", "random_button", randomArtwork.id);
      }
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title="Privacy Policy - Ariadne's Gallery"
        description="Privacy policy for Ariadne's Gallery. Learn how we collect, use, and protect your information."
        url="https://ariadnesgallery.com/privacy-policy/"
        type="website"
      />

      <SearchHeader
        artworks={searchResults}
        onArtworkSelect={handleArtworkSelect}
        onSearch={handleSearch}
        totalCount={comprehensiveArtworks.length}
        onRandomArtwork={handleRandomArtwork}
      />

      <main className="pt-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="prose prose-lg max-w-none">
            <h1 className="text-4xl font-bold mb-8">Privacy Policy</h1>
            <p className="text-sm text-gray-500 mb-8">Last updated: April 11, 2026</p>

            <p className="mb-6">
              Ariadne's Gallery ("we", "us", or "our") operates the website{" "}
              <strong>ariadnesgallery.com</strong>. This Privacy Policy explains
              what information we collect, how we use it, and your choices
              regarding your data.
            </p>

            <h2 className="text-2xl font-semibold mb-4">Information We Collect</h2>

            <h3 className="text-xl font-semibold mb-3">Email Address</h3>
            <p className="mb-6">
              If you subscribe to our Weekly Art Digest newsletter, we collect
              your email address. This is the only personal information we
              collect directly from you.
            </p>

            <h3 className="text-xl font-semibold mb-3">Usage Data</h3>
            <p className="mb-6">
              We use Google Analytics to collect anonymous usage data such as
              pages visited, time spent on the site, browser type, and
              referring URLs. This data is aggregated and cannot be used to
              identify you personally.
            </p>

            <h3 className="text-xl font-semibold mb-3">Cookies</h3>
            <p className="mb-6">
              Our site uses cookies set by Google Analytics to distinguish
              unique users and throttle request rates. These are standard
              analytics cookies and do not contain personal information.
            </p>

            <h2 className="text-2xl font-semibold mb-4">How We Use Your Information</h2>
            <ul className="list-disc list-inside mb-8 space-y-2">
              <li>
                <strong>Newsletter:</strong> Your email address is used solely to
                send you our Weekly Art Digest and occasional notifications about
                new content on the site. You can unsubscribe at any time using the
                link in every email.
              </li>
              <li>
                <strong>Analytics:</strong> Usage data helps us understand how
                visitors interact with the site so we can improve the experience.
              </li>
            </ul>

            <h2 className="text-2xl font-semibold mb-4">Third-Party Services</h2>
            <p className="mb-4">We use the following third-party services:</p>
            <ul className="list-disc list-inside mb-8 space-y-2">
              <li>
                <strong>Netlify</strong> &mdash; Website hosting and serverless
                functions.{" "}
                <a href="https://www.netlify.com/privacy/" className="text-indigo-600 hover:text-indigo-800" target="_blank" rel="noopener noreferrer">
                  Netlify Privacy Policy
                </a>
              </li>
              <li>
                <strong>Google Analytics</strong> &mdash; Anonymous website
                analytics.{" "}
                <a href="https://policies.google.com/privacy" className="text-indigo-600 hover:text-indigo-800" target="_blank" rel="noopener noreferrer">
                  Google Privacy Policy
                </a>
              </li>
              <li>
                <strong>Resend</strong> &mdash; Email delivery for our newsletter.{" "}
                <a href="https://resend.com/legal/privacy-policy" className="text-indigo-600 hover:text-indigo-800" target="_blank" rel="noopener noreferrer">
                  Resend Privacy Policy
                </a>
              </li>
            </ul>

            <h2 className="text-2xl font-semibold mb-4">Data Retention</h2>
            <p className="mb-6">
              Your email address is retained for as long as you remain subscribed
              to our newsletter. You can unsubscribe at any time, and your email
              will be removed from our mailing list. Analytics data is retained
              according to Google Analytics' standard retention policies.
            </p>

            <h2 className="text-2xl font-semibold mb-4">Your Rights</h2>
            <p className="mb-6">
              You have the right to:
            </p>
            <ul className="list-disc list-inside mb-8 space-y-2">
              <li>Unsubscribe from our newsletter at any time</li>
              <li>Request deletion of your email address from our records</li>
              <li>Request information about what data we hold about you</li>
            </ul>

            <h2 className="text-2xl font-semibold mb-4">Children's Privacy</h2>
            <p className="mb-6">
              Our site is not directed at children under 13. We do not knowingly
              collect personal information from children.
            </p>

            <h2 className="text-2xl font-semibold mb-4">Changes to This Policy</h2>
            <p className="mb-6">
              We may update this Privacy Policy from time to time. Changes will
              be posted on this page with an updated revision date.
            </p>

          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
